import Vue from 'vue'
import Constants from 'src/utilities/constants'
import {OK} from 'http-status-codes'
import {make} from 'vuex-pathify'
import {assemblePlanNameDisplay, objectById, searchFieldsHaveValues, sortBy, sortByName} from 'src/utilities/utils'
import {esClient, multiMatch, planNameFilterBuilder} from 'src/utilities/ptracSearch'
import {byId, crudifyMutations, hydrateEdit, resetResults, savePayload, updateResult} from 'src/utilities/vuex2'
import {ptracGetEntities} from 'src/utilities/ptracApi'
import {createAndDownloadWorksheet} from 'src/utilities/excel'
import _ from 'lodash'

const edit = {
  id: null,
  name: null,
  number: null,
  hand: null,
  builderId: null,
  fileId: null,
  energyPlanId: null,
  active: true
}

const query = {
  search: null,
  builderId: null,
  active: true
}

const initialState = {
  autocompleteResults: [],
  inUse: null,
  edit: JSON.parse(JSON.stringify(edit)),
  query: JSON.parse(JSON.stringify(query)),
  results: []
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
getters.byId = byId
getters.savePayload = savePayload
getters.autocompleteResults = (state) => {
  const results = []

  state.autocompleteResults.forEach(planName => {
    if (!planName.builder) {
      planName.builder = { name: 'Error Loading Builder' }
    }
    let name = planName.builder.name + ': '

    if (planName.name && planName.number) {
      name += planName.name + ' - ' + planName.number
    } else if (planName.name) {
      name += planName.name
    } else if (planName.number) {
      name += planName.number
    }

    if (planName.hand) {
      name += ' - ' + planName.hand
    }

    results.push({
      fileId: planName.fileId,
      energyPlanId: planName.energyPlanId,
      id: planName.id,
      name
    })
  })

  return sortByName(results)
}

getters.resultsDisplay = (state) => {
  const planNames = []
  state.results.forEach((planName) => {
    planNames.push({
      plan: assemblePlanNameDisplay(planName),
      id: planName.id
    })
  })
  return sortBy(planNames, 'plan')
}

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'search',
    'builderId'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

const mutations = make.mutations(state)
crudifyMutations(mutations)
mutations.resetResults = resetResults
mutations.updateResult = updateResult
mutations.hydrateEdit = hydrateEdit
mutations.resetEdit = (state) => {
  state.edit = JSON.parse(JSON.stringify(initialState.edit))
}

mutations.resetQuery = (state) => {
  Vue.set(state.query, 'search',  null)
  Vue.set(state.query, 'builderId',  null)
}
mutations.resetBuilderId = (state) => {
  Vue.set(state.query, 'builderId',  null)
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async fetchByNameOrNumberStartsWithFloorPlans({dispatch, state}, query) {
      const response = await ptracGetEntities(`plan-names/search/findByNameStartsWithIgnoreCaseOrNumberStartsWithIgnoreCaseAndFileIdNotNullOrderByNameAsc?name=${query}&number=${query}`)

      if (OK === response.status) {
        const builderIds = _.map(response.data, 'builderId')

        const companiesResponse = await dispatch('fetchByIds', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
          ids: builderIds
        }, { root: true})

        if (OK === companiesResponse.status) {
          response.data.forEach(planName => {
            planName.builder = objectById(companiesResponse.data, planName.builderId)
          })
        }

        state.autocompleteResults = response.data
      }
    },
    async fetchByNameOrNumberStartsWithEnergyPlans({dispatch, state}, query) {
      const response = await ptracGetEntities(`plan-names/search/findByNameStartsWithIgnoreCaseOrNumberStartsWithIgnoreCaseAndEnergyPlanIdNotNullOrderByNameAsc?name=${query}&number=${query}`)

      if (OK === response.status) {
        const builderIds = _.map(response.data, 'builderId')

        const companiesResponse = await dispatch('fetchByIds', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
          ids: builderIds
        }, { root: true})

        if (OK === companiesResponse.status) {
          response.data.forEach(planName => {
            planName.builder = objectById(companiesResponse.data, planName.builderId)
          })
        }

        state.autocompleteResults = response.data
      }
    },
    async fetchFindByBuilderId({}, companyId) {
      return await ptracGetEntities('plan-names/search/findByBuilderId?builderId=' + companyId)
    },
    async fetchByNameOrNumberAndBuilderId({commit}, payload) {
      const response = await ptracGetEntities(`plan-names/search/findByNameLikeIgnoreCaseOrNumberLikeIgnoreCaseAndBuilderId?name=${payload.name}&number=${payload.number}&builderId=${payload.builderId}`)

      if (OK === response.status) {
        commit('results', response.data)
      }

      return response
    },
    async fetchByBuilderIdWithFile({}, builderId) {
      return await ptracGetEntities(`plan-names/search/findByBuilderIdAndActiveAndFileIdNotNullOrderByNameAsc?builderId=${builderId}&active=true`)
    },
    async fetchByBuilderIdWithEnergyPlan({}, builderId) {
      return await ptracGetEntities(`plan-names/search/findByBuilderIdAndActiveAndEnergyPlanIdNotNullOrderByNameAsc?builderId=${builderId}&active=true`)
    },
    async fetchElasticSearchResults({commit, state, dispatch, rootGetters}) {
      const filterFields = [
        'active',
        'builderId'
      ]

      const searchFields = [
        'name',
        'number'
      ]

      let searchBody = {
        size: Constants.ELASTICSEARCH.MAX_SEARCH_RESULTS,
        query: {
          bool: {
            must: [
              {match: {_class: Constants.ENTITY_FIELDS.PLAN_NAME.CLASS}}
            ],
            should: [],
            filter: []
          }
        }
      }

      // Partial & Exact queries
      if(state.query.search) {
        searchBody.query.bool.should.push(multiMatch(searchFields, state.query.search.split(' ')))
        searchBody.query.bool.minimum_should_match = 1
      }

      // Filter
      searchBody.query.bool.filter.push(...planNameFilterBuilder(filterFields, state.query))

      const esCreds = rootGetters['config/byName'](Constants.CONFIGS.ELASTICSEARCH_UN_PW)
      let response = await
        esClient(esCreds).search({
          index: 'plan-names',
          body: searchBody
        })
      const hits = response.hits.hits

      // Get builder name from id, then filter out duplicates
      const builderIds = _.map(hits, '_source.builderId')
      const uniqueBuilderIds = [... new Set(builderIds)]
      const companiesResponse = await dispatch('fetchByIds', {
        endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
        ids: uniqueBuilderIds
      }, { root: true})

      if (OK === companiesResponse.status) {
        for (const hit of hits) {
          const builder = objectById(companiesResponse.data, hit._source.builderId)
          if (!builder) {
            console.error('Failed to load builder by ID: ' + hit._source.builderId)
            continue
          }
          hit._source.builderName = objectById(companiesResponse.data, hit._source.builderId).name
        }
      }

      // Get File Names from File Ids
      let fileIds = _.map(hits, '_source.fileId')
      fileIds = fileIds.filter((a) => a) // remove false-y / empty values
      fileIds = [... new Set(fileIds)] // remove duplicates
      const filesResponse = await dispatch('fetchByIds', {
        endpoint: 'files',
        ids: fileIds,
        whichEntity: 'ptracFiles'
      }, { root: true })

      if (OK === filesResponse.status) {
        for (const hit of hits) {
          if (hit._source.fileId && objectById(filesResponse.data, hit._source.fileId)) {
            hit._source.fileName = objectById(filesResponse.data, hit._source.fileId).name
          }
        }
      }


      let energyPlanIds = _.map(hits, '_source.energyPlanId')
      energyPlanIds = energyPlanIds.filter((a) => a) // remove false-y / empty values
      energyPlanIds = [... new Set(energyPlanIds)] // remove duplicates
      const energyPlansResponse = await dispatch('fetchByIds', {
        endpoint: 'files',
        ids: energyPlanIds,
        whichEntity: 'ptracFiles'
      }, { root: true })

      if (OK === energyPlansResponse.status) {
        for (const hit of hits) {
          if (hit._source.energyPlanId && objectById(energyPlansResponse.data, hit._source.energyPlanId)) {
            hit._source.energyPlanName = objectById(energyPlansResponse.data, hit._source.energyPlanId).name
          }
        }
      }

      for (const hit of hits) {
        hit._source.id = hit._id
      }

      commit('results', hits)

      return response
    },
    async fileInUse({commit, dispatch}, file) {
      const response = await ptracGetEntities(`plan-names/search/findByFileIdAndActiveOrderByNameAsc?fileId=${file.id}&active=true`)

      if (OK === response.status) {
        const builderIds = _.map(response.data, 'builderId')
        const companiesResponse = await dispatch('fetchByIds', {
          endpoint: Constants.ENTITY_ENDPOINTS.COMPANIES,
          ids: builderIds
        }, { root: true})

        if (OK === companiesResponse.status) {
          response.data.forEach(planName => {
            planName.builder = objectById(companiesResponse.data, planName.builderId)
          })
        }

        commit('inUse', sortByName(response.data))
      }

      return response
    },
    async resultsToExcel({state, rootState}) {
      const fileName = 'plan-names-results.xlsx'
      const tabName = 'Plan Names'
      const header = [
        {
          header: 'Name',
          key: 'name',
          width: 20
        }, {
          header: 'Number',
          key: 'number',
          width: 20
        }, {
          header: 'Hand',
          key: 'hand',
          width: 20
        }, {
          header: 'Builder',
          key: 'builder',
          width: 30
        }, {
          header: 'File',
          key: 'file',
          width: 40
        }]

      const planNames = _.map(state.results, '_source')

      const rows = []

      planNames.forEach((planName) => {
        rows.push(
          {
            name: planName.name,
            number: planName.number,
            hand: planName.hand,
            builder: planName.builderName || null,
            file: planName.fileName || null
          }
        )
      })

      const user = rootState.security.user

      createAndDownloadWorksheet(fileName, tabName, header, rows, user)
    }
  }
}
